/* Page-local product state. Shopify resolves option combinations; measurements
   stay in the same form while option markup and the color description refresh.
   The scoped submit handler stops propagation to the shared drawer's global add handler. */
(() => {
  'use strict';
  if (customElements.get('catalog-product')) return;
  class CatalogProduct extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.dialog = this.querySelector('.made-product__lightbox');
      this.sizeGuide = this.querySelector('[data-size-guide]');
      this.addEventListener('click', (event) => {
        const back = event.target.closest('.made-product__back');
        if (back && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
          if (this.canStepBack(document.referrer, history.length)) {
            event.preventDefault();
            history.back();
            return;
          }
        }
        const option = event.target.closest('[data-option-link]');
        if (option && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
          event.preventDefault(); if (!this.busy) this.selectOption(option);
        }
        const zoom = event.target.closest('[data-zoom]');
        if (zoom && zoom.closest('.made-product__media-track')) {
          event.preventDefault();
          this.zoomTrigger = zoom;
          this.openLightbox(this.visibleZoomImages().indexOf(zoom));
        }
        if (event.target.closest('[data-zoom-close]')) {
          event.preventDefault();
          this.dialog?.close();
        }
        if (event.target.closest('[data-zoom-prev]')) {
          event.preventDefault();
          this.showZoom(this.zoomIndex - 1);
        }
        if (event.target.closest('[data-zoom-next]')) {
          event.preventDefault();
          this.showZoom(this.zoomIndex + 1);
        }
        const thumb = event.target.closest('[data-lightbox-thumb]');
        if (thumb) {
          event.preventDefault();
          const images = this.visibleZoomImages();
          const index = images.findIndex((node) => node.closest('.made-product__media')?.dataset.mediaId === thumb.dataset.mediaId);
          if (index >= 0) this.showZoom(index);
        } else if (event.target.closest('[data-lightbox-photo]')) {
          event.preventDefault();
          if (matchMedia('(min-width: 990px)').matches) this.toggleLightboxZoom();
        }
        const mediaLink = event.target.closest('.made-product__media-nav a');
        if (mediaLink) {
          event.preventDefault(); this.showMedia(mediaLink.hash.split('-').pop());
        }
        const tab = event.target.closest('.made-product__tab');
        if (tab && !event.target.closest('[data-size-guide]')) {
          event.preventDefault();
          this.selectTab(tab);
        }
        if (event.target.closest('[data-help-open]')) {
          event.preventDefault();
          this.openHelp();
          return;
        }
        if (event.target.closest('[data-help-close]')) {
          event.preventDefault();
          this.closeHelp();
          return;
        }
        if (event.target.closest('[data-help-remove]')) {
          event.preventDefault();
          this.removeHelpProduct();
          return;
        }
        if (event.target.closest('[data-size-guide-open]')) {
          event.preventDefault();
          this.openSizeGuide();
        }
        if (event.target.closest('[data-size-guide-close]') || event.target.closest('[data-size-guide-dismiss]')) {
          event.preventDefault();
          this.closeSizeGuide();
        }
      }, { signal });
      this.sizeGuide?.addEventListener('click', (event) => {
        if (event.target === this.sizeGuide) this.closeSizeGuide();
      }, { signal });
      this.sizeGuide?.addEventListener('cancel', () => this.unlockSizeGuide(), { signal });
      this.sizeGuide?.addEventListener('close', () => this.unlockSizeGuide(), { signal });
      this.sizeGuide?.querySelectorAll('[data-size-unit]').forEach((input) => {
        input.addEventListener('change', () => this.syncSizeGuideUnits(), { signal });
      });
      this.syncSizeGuideUnits();
      this.dialog?.addEventListener('close', () => {
        this.dialog?.classList.remove('is-zoomed');
        this.resetLightboxPinch();
        document.documentElement.classList.remove('lightbox-open');
        this.zoomTrigger?.focus({ preventScroll: true });
      }, { signal });
      this.bindLightboxPinch();
      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.showZoom(this.zoomIndex + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      }, { signal });
      this.addEventListener('pointermove', (event) => this.moveGalleryPointer(event), { signal });
      this.addEventListener('pointerleave', () => this.querySelector('.made-product__gallery')?.classList.remove('is-hovering-media'), { signal });
      this.form.addEventListener('submit', (event) => this.addToCart(event), { signal });
      this.querySelector('.help-dialog__form')?.addEventListener('submit', (event) => this.submitHelp(event), { signal });
      this.querySelector('.help-dialog__form')?.addEventListener('input', (event) => {
        if (event.target.matches('[data-help-email], [data-help-confirm]')) {
          this.querySelector('[data-help-confirm]')?.setCustomValidity('');
        }
      }, { signal });
      const helpDialog = this.querySelector('[data-help-dialog]');
      helpDialog?.addEventListener('click', (event) => {
        if (event.target === helpDialog) helpDialog.close();
      }, { signal });
      helpDialog?.addEventListener('close', () => {
        this.finishHelpResult();
        this.querySelector('[data-help-open]')?.focus({ preventScroll: true });
      }, { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-qty-step]');
        if (button) this.stepQuantity(button);
        this.settleYarn(event.target.closest('.mto-btn'));
      }, { signal });
      this.addEventListener('pointerout', (event) => {
        const yarn = event.target.closest?.('.mto-btn');
        if (!yarn || yarn.contains(event.relatedTarget) || yarn.disabled) return;
        yarn.classList.remove('is-settled');
      }, { signal });
      this.addEventListener('focusout', (event) => {
        const yarn = event.target.closest?.('.mto-btn');
        if (!yarn || yarn.contains(event.relatedTarget) || yarn.disabled || yarn.matches(':hover')) return;
        yarn.classList.remove('is-settled');
      }, { signal });
      window.addEventListener('popstate', () => location.reload(), { signal });
      this.syncGalleryChrome();
      window.addEventListener('resize', () => this.syncGalleryChrome(), { signal });
      window.addEventListener('scroll', () => this.onPinnedScroll(), { signal, passive: true });
      if (window.ResizeObserver) {
        this.pinObserver = new ResizeObserver(() => this.syncPinnedColumns());
        const infoScroll = this.querySelector('.made-product__info-scroll');
        const track = this.querySelector('.made-product__media-track');
        if (infoScroll) this.pinObserver.observe(infoScroll);
        if (track) this.pinObserver.observe(track);
      }
      this.syncState();
      if (this.dataset.featuredMediaId) this.applyMediaGroup(this.dataset.featuredMediaId, { scroll: false });
      else this.observeGallery();
      const helpSuccess = this.querySelector('[data-help-success]');
      if (helpSuccess && !helpSuccess.hidden) {
        this.openHelp();
        this.clearContactPostedUrl();
      } else if (this.querySelector('[data-help-errors]')) {
        this.openHelp();
      }
      if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (error) => {
          if (!error && this.isConnected) this.querySelectorAll('model-viewer').forEach((model) => {
            if (!model.dataset.initialized) { new window.Shopify.ModelViewerUI(model); model.dataset.initialized = 'true'; }
          });
        } }]);
      }
    }
    settleYarn(button) {
      if (!button || !window.matchMedia('(hover: hover)').matches) return;
      button.classList.add('is-settled');
    }
    canStepBack(referrer, historyLength) {
      if (!(historyLength > 1) || !referrer) return false;
      try {
        return new URL(referrer).origin === location.origin;
      } catch {
        return false;
      }
    }
    syncState(options = {}) {
      const state = this.querySelector('[data-option-state]');
      this.querySelectorAll('[data-product-submit]').forEach((button) => {
        button.disabled = this.busy || this.loading || button.dataset.purchaseEnabled !== 'true' || state.dataset.available !== 'true';
        const labelText = !state.dataset.variantId ? button.dataset.unavailableLabel : state.dataset.available === 'true' ? button.dataset.availableLabel : button.dataset.soldOutLabel;
        const label = button.querySelector('.mto-btn__label');
        if (label) label.textContent = labelText;
        else button.textContent = labelText;
        const stillEngaged = button.matches(':hover') || document.activeElement === button;
        if (button.classList.contains('is-settled') && !button.disabled && !stillEngaged) button.classList.remove('is-settled');
      });
      if (options.followMedia && state.dataset.mediaId) this.applyMediaGroup(state.dataset.mediaId);
    }
    async selectOption(link) {
      this.optionRequest?.abort();
      this.optionRequest = new AbortController();
      const request = this.optionRequest;
      this.loading = true; this.syncState();
      const url = new URL(link.href);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.querySelector('[data-option-state]').setAttribute('aria-busy', 'true');
      this.showError('');
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Option request failed');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const state = parsed.querySelector('[data-option-state]');
        if (!state) throw new Error('Missing product state');
        if (state.dataset.productId !== this.dataset.productId) {
          url.searchParams.delete('section_id'); location.assign(url); return;
        }
        if (request.signal.aborted) return;
        this.querySelector('[data-option-state]').replaceWith(state);
        this.syncHelpProduct(state);
        const nextDescription = parsed.querySelector('[data-product-description]');
        const description = this.querySelector('[data-product-description]');
        if (nextDescription && description) description.innerHTML = nextDescription.innerHTML;
        url.searchParams.delete('section_id');
        history.pushState({}, '', url);
        state.querySelector(`[data-option-id="${link.dataset.optionId}"]`)?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') this.showError(this.dataset.optionError);
      } finally {
        if (request === this.optionRequest && this.isConnected) {
          this.loading = false;
          this.querySelector('[data-option-state]').removeAttribute('aria-busy');
          this.syncState({ followMedia: true });
        }
      }
    }
    async addToCart(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.busy || this.loading || this.querySelector('[data-product-submit]')?.disabled || !this.form.reportValidity()) return;
      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location?.pathname || '/');
      this.busy = true; this.syncState(); this.showError('');
      this.querySelectorAll('[data-product-submit]').forEach((button) => button.setAttribute('aria-busy', 'true'));
      const root = window.Shopify?.routes?.root || '/';
      let rejection;
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok) {
          rejection = result.description || result.message || this.dataset.cartError;
          throw new Error(rejection);
        }
        // Never retry a successful add when a drawer refresh fails.
        if (window.CartDrawer && document.getElementById('CartDrawer')) {
          try { await window.CartDrawer.refresh(result.sections); window.CartDrawer.open(); }
          catch { location.assign(`${root}cart`); }
        } else location.assign(`${root}cart`);
      } catch (error) {
        this.showError(rejection || this.dataset.cartError);
      } finally {
        this.busy = false; this.syncState();
        this.querySelectorAll('[data-product-submit]').forEach((button) => button.removeAttribute('aria-busy'));
      }
    }
    showError(message) {
      this.querySelectorAll('[data-cart-error]').forEach((node) => { node.textContent = message; node.hidden = !message; });
    }
    visibleZoomImages() {
      return [...this.querySelectorAll('.made-product__media-track .made-product__media:not([hidden]) [data-zoom]')];
    }
    applyMediaGroup(mediaId, options = {}) {
      const track = this.querySelector('.made-product__media-track');
      if (!track) return;
      const nodes = [...track.querySelectorAll('.made-product__media')];
      const current = nodes.find((node) => node.dataset.mediaId === String(mediaId));
      const group = current?.dataset.mediaGroup || '';
      const grouped = nodes.some((node) => node.dataset.mediaGroup);
      if (grouped && group && group !== 'all-media') {
        nodes.forEach((node) => {
          node.hidden = node.dataset.mediaGroup !== group;
        });
        this.querySelectorAll('.made-product__media-nav a').forEach((link) => {
          const media = nodes.find((node) => node.dataset.mediaId === link.dataset.mediaId);
          link.hidden = !media || media.hidden;
        });
        this.querySelectorAll('[data-lightbox-thumb]').forEach((thumb) => {
          const media = nodes.find((node) => node.dataset.mediaId === thumb.dataset.mediaId);
          const hide = !media || media.hidden;
          thumb.dataset.groupHidden = hide ? 'true' : 'false';
          thumb.hidden = hide;
        });
        const nav = this.querySelector('.made-product__media-nav');
        if (nav) nav.hidden = [...nav.querySelectorAll('a')].filter((link) => !link.hidden).length < 2;
        const zoomCount = this.visibleZoomImages().length;
        const thumbs = this.querySelector('[data-lightbox-thumbs]');
        if (thumbs) thumbs.hidden = zoomCount < 2;
        const prev = this.querySelector('[data-zoom-prev]');
        const next = this.querySelector('[data-zoom-next]');
        if (prev) prev.hidden = zoomCount < 2;
        if (next) next.hidden = zoomCount < 2;
      }
      this.observeGallery();
      if (mediaId && options.scroll !== false) this.showMedia(String(mediaId));
      else this.syncPinnedColumns();
    }
    showMedia(id) {
      const track = this.querySelector('.made-product__media-track');
      const media = [...track.children].find((node) => node.dataset.mediaId === id);
      if (!media) return;
      const desktop = matchMedia('(min-width: 990px)').matches;
      const rect = media.getBoundingClientRect();
      if (desktop) {
        if (this.classList.contains('made-product--detail')) {
          this.scrollPinnedToMedia(media);
          return;
        }
        window.scrollTo({ top: rect.top + window.scrollY, left: 0, behavior: 'auto' });
        return;
      }
      const bounds = track.getBoundingClientRect();
      track.scrollTo({ left: track.scrollLeft + rect.left - bounds.left, top: 0, behavior: 'auto' });
    }
    observeGallery() {
      this.galleryObserver?.disconnect();
      const desktop = matchMedia('(min-width: 990px)').matches;
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.galleryObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const bounds = entry.rootBounds;
          const rootArea = bounds ? bounds.width * bounds.height : 0;
          const seen = entry.intersectionRect.width * entry.intersectionRect.height;
          const inView = !entry.target.hidden && seen > 0 && (entry.intersectionRatio >= 0.35 || (rootArea > 0 && seen / rootArea >= 0.3));
          entry.target.querySelectorAll('video').forEach((video) => {
            if (inView && !reduceMotion) this.playGalleryVideo(video);
            else video.pause();
          });
          if (entry.intersectionRatio < 0.6) return;
          this.querySelectorAll('.made-product__media-nav a').forEach((link) => {
            if (link.hash === `#${entry.target.id}`) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
          });
        });
      }, { root: desktop ? null : this.querySelector('.made-product__media-track'), threshold: [0, 0.2, 0.6] });
      this.querySelectorAll('.made-product__media').forEach((node) => {
        if (node.hidden) {
          node.querySelectorAll('video').forEach((video) => video.pause());
          return;
        }
        this.galleryObserver.observe(node);
      });
    }
    playGalleryVideo(video) {
      video.loop = true;
      video.playsInline = true;
      const start = video.play();
      if (!start) return;
      start.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }
    syncGalleryChrome() {
      this.normalizeDetailTabs();
      const gallery = this.querySelector('.made-product__gallery');
      const track = this.querySelector('.made-product__media-track');
      if (!gallery || !track) return;
      track.style.height = '';
      this.syncPinnedColumns();
      this.observeGallery();
    }
    usesPinnedColumns() {
      return matchMedia('(min-width: 990px)').matches && this.classList.contains('made-product--detail');
    }
    stepQuantity(button) {
      const input = button.closest('.made-product__qty')?.querySelector('.made-product__qty-input');
      if (!input) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max === '' ? Infinity : Number(input.max);
      const next = (Number(input.value) || min) + step * Number(button.dataset.qtyStep);
      input.value = String(Math.min(max, Math.max(min, next)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    onPinnedScroll() {
      if (this.pinFrame) return;
      this.pinFrame = requestAnimationFrame(() => {
        this.pinFrame = 0;
        this.applyPinnedShift();
      });
    }
    pinnedRanges() {
      const gallery = this.querySelector('.made-product__gallery');
      const track = this.querySelector('.made-product__media-track');
      const info = this.querySelector('.made-product__info');
      const infoScroll = this.querySelector('.made-product__info-scroll');
      if (!gallery || !track || !info || !infoScroll) return null;
      const view = gallery.clientHeight;
      if (!view) return null;
      return {
        view,
        infoExtra: Math.max(0, infoScroll.offsetHeight - info.clientHeight),
        galleryExtra: Math.max(0, track.offsetHeight - view),
      };
    }
    syncPinnedColumns() {
      const main = this.querySelector('.made-product__main');
      const desktop = this.usesPinnedColumns();
      if (!desktop || !main) {
        this.clearPinnedColumns();
        return;
      }
      const ranges = this.pinnedRanges();
      if (!ranges) return;
      const stage = ranges.view + ranges.infoExtra + ranges.galleryExtra;
      if (this.pinStage !== stage) {
        this.pinStage = stage;
        main.style.setProperty('--pdp-stage', `${stage}px`);
      }
      this.applyPinnedShift();
    }
    applyPinnedShift() {
      if (!this.usesPinnedColumns()) return;
      const main = this.querySelector('.made-product__main');
      const track = this.querySelector('.made-product__media-track');
      const infoScroll = this.querySelector('.made-product__info-scroll');
      const ranges = this.pinnedRanges();
      if (!main || !track || !infoScroll || !ranges) return;
      const start = main.getBoundingClientRect().top + window.scrollY;
      const total = ranges.infoExtra + ranges.galleryExtra;
      const scrolled = Math.min(Math.max(window.scrollY - start, 0), total);
      const infoShift = Math.min(scrolled, ranges.infoExtra);
      const galleryShift = Math.max(0, scrolled - ranges.infoExtra);
      infoScroll.style.transform = infoShift ? `translateY(${-infoShift}px)` : '';
      track.style.transform = galleryShift ? `translateY(${-galleryShift}px)` : '';
    }
    scrollPinnedToMedia(media) {
      const main = this.querySelector('.made-product__main');
      const ranges = this.pinnedRanges();
      if (!main || !ranges) return;
      const desired = Math.min(Math.max(media.offsetTop, 0), ranges.galleryExtra);
      const start = main.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: start + ranges.infoExtra + desired, left: 0, behavior: 'auto' });
    }
    clearPinnedColumns() {
      const main = this.querySelector('.made-product__main');
      const track = this.querySelector('.made-product__media-track');
      const infoScroll = this.querySelector('.made-product__info-scroll');
      if (main) main.style.removeProperty('--pdp-stage');
      if (track) track.style.transform = '';
      if (infoScroll) infoScroll.style.transform = '';
      this.pinStage = 0;
    }
    selectTab(tab) {
      const open = tab.getAttribute('aria-expanded') === 'true';
      tab.setAttribute('aria-expanded', open ? 'false' : 'true');
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = open;
      this.syncPinnedColumns();
    }
    normalizeDetailTabs() {}
    openSizeGuide() {
      if (!this.sizeGuide?.showModal || this.sizeGuide.open) return;
      const panel = this.sizeGuide.querySelector('.size-guide__panel');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      document.documentElement.classList.add('size-guide-open');
      this.sizeGuide.classList.remove('is-open');
      if (panel && !reduce) {
        panel.style.transition = 'none';
        panel.style.visibility = 'hidden';
      }
      this.sizeGuide.showModal();
      if (reduce || !panel) {
        this.sizeGuide.classList.add('is-open');
      } else {
        panel.offsetWidth;
        panel.style.visibility = '';
        requestAnimationFrame(() => {
          if (!this.sizeGuide?.open) return;
          panel.style.transition = '';
          this.sizeGuide.classList.add('is-open');
        });
      }
      this.sizeGuide.querySelector('[data-size-guide-close]')?.focus({ preventScroll: true });
      this.syncSizeGuideUnits();
    }
    closeSizeGuide() {
      if (!this.sizeGuide?.open || this.sizeGuideClosing) return;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || !this.sizeGuide.classList.contains('is-open')) {
        this.sizeGuide.close();
        return;
      }
      this.sizeGuideClosing = true;
      const panel = this.sizeGuide.querySelector('.size-guide__panel');
      const finish = () => {
        if (!this.sizeGuideClosing) return;
        this.sizeGuideClosing = false;
        this.sizeGuide.close();
      };
      panel?.addEventListener('transitionend', (event) => {
        if (event.target === panel && event.propertyName === 'transform') finish();
      }, { once: true });
      this.sizeGuide.classList.remove('is-open');
      setTimeout(finish, 500);
    }
    unlockSizeGuide() {
      this.sizeGuideClosing = false;
      this.sizeGuide?.classList.remove('is-open');
      document.documentElement.classList.remove('size-guide-open');
      this.querySelector('[data-size-guide-open]')?.focus({ preventScroll: true });
    }
    syncSizeGuideUnits() {
      if (!this.sizeGuide) return;
      const unit = this.sizeGuide.querySelector('[data-size-unit]:checked')?.value || 'inch';
      const source = this.sizeGuide.dataset.sourceUnit || 'inch';
      this.sizeGuide.querySelectorAll('[data-size-values]').forEach((cell) => {
        const parts = (cell.dataset.sizeValues || '')
          .split(',')
          .map((raw) => this.convertSize(raw, source, unit))
          .filter((part) => part !== '');
        cell.textContent = parts.join(' - ');
      });
    }
    convertSize(raw, from, to) {
      const value = Number.parseFloat(raw);
      if (!Number.isFinite(value)) return '';
      const converted = from === to ? value : (from === 'cm' ? value / 2.54 : value * 2.54);
      return to === 'cm' ? converted.toFixed(0) : converted.toFixed(1);
    }
    moveGalleryPointer(event) {
      const gallery = this.querySelector('.made-product__gallery');
      const plus = this.querySelector('[data-zoom-plus]');
      const media = event.target.closest('.made-product__media');
      if (!gallery || !plus) return;
      if (this.dialog?.open || !media?.querySelector('[data-zoom]')) {
        gallery.classList.remove('is-hovering-media');
        return;
      }
      const rect = gallery.getBoundingClientRect();
      plus.style.left = `${event.clientX - rect.left}px`;
      plus.style.top = `${event.clientY - rect.top}px`;
      gallery.classList.add('is-hovering-media');
    }
    openLightbox(index) {
      if (!this.dialog?.showModal) return;
      document.documentElement.classList.add('lightbox-open');
      this.showZoom(index);
      if (!this.dialog.open) this.dialog.showModal();
      this.querySelector('.made-product__gallery')?.classList.remove('is-hovering-media');
      this.dialog.querySelector('[data-zoom-close]')?.focus({ preventScroll: true });
    }
    showZoom(index) {
      const images = this.visibleZoomImages();
      if (!images.length) return;
      this.zoomIndex = ((index % images.length) + images.length) % images.length;
      const source = images[this.zoomIndex];
      const image = this.querySelector('[data-zoom-image]');
      if (image && source) {
        image.src = source.href;
        image.alt = source.dataset.alt || '';
      }
      this.dialog?.classList.remove('is-zoomed');
      this.resetLightboxPinch();
      this.syncLightboxThumbs();
    }
    syncLightboxThumbs() {
      const thumbs = [...this.querySelectorAll('[data-lightbox-thumb]')];
      if (!thumbs.length) return;
      const activeId = this.visibleZoomImages()[this.zoomIndex]?.closest('.made-product__media')?.dataset.mediaId;
      const inGroup = thumbs.filter((thumb) => thumb.dataset.groupHidden !== 'true');
      const activeIndex = inGroup.findIndex((thumb) => thumb.dataset.mediaId === activeId);
      const windowSize = 7;
      let start = 0;
      if (inGroup.length > windowSize) {
        const center = activeIndex < 0 ? 0 : activeIndex;
        start = Math.min(Math.max(center - Math.floor(windowSize / 2), 0), inGroup.length - windowSize);
      }
      thumbs.forEach((thumb) => {
        if (thumb.dataset.groupHidden === 'true') {
          thumb.hidden = true;
          thumb.removeAttribute('aria-current');
          return;
        }
        const index = inGroup.indexOf(thumb);
        thumb.hidden = index < start || index >= start + windowSize;
        if (thumb.dataset.mediaId === activeId) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
    toggleLightboxZoom() {
      if (!this.dialog?.open) return;
      this.dialog.classList.toggle('is-zoomed');
    }
    bindLightboxPinch() {
      const photo = this.dialog?.querySelector('[data-lightbox-photo]');
      if (!photo) return;
      const { signal } = this.events;
      photo.addEventListener('touchstart', (event) => this.onLightboxTouchStart(event), { signal, passive: false });
      photo.addEventListener('touchmove', (event) => this.onLightboxTouchMove(event), { signal, passive: false });
      photo.addEventListener('touchend', (event) => this.onLightboxTouchEnd(event), { signal });
      photo.addEventListener('touchcancel', (event) => this.onLightboxTouchEnd(event), { signal });
    }
    lightboxTouchDistance(event) {
      const [a, b] = event.touches;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
    onLightboxTouchStart(event) {
      if (matchMedia('(min-width: 990px)').matches) return;
      if (event.touches.length === 2) {
        event.preventDefault();
        this.lightboxPinching = true;
        this.lightboxStartDistance = this.lightboxTouchDistance(event);
        this.lightboxStartScale = this.lightboxScale || 1;
        this.lightboxPan = null;
      } else if (event.touches.length === 1 && (this.lightboxScale || 1) > 1) {
        this.lightboxPan = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
          originX: this.lightboxX || 0,
          originY: this.lightboxY || 0
        };
      }
    }
    onLightboxTouchMove(event) {
      if (matchMedia('(min-width: 990px)').matches) return;
      if (event.touches.length === 2 && this.lightboxPinching) {
        event.preventDefault();
        const scale = (this.lightboxStartScale || 1) * (this.lightboxTouchDistance(event) / this.lightboxStartDistance);
        this.lightboxScale = Math.min(4, Math.max(1, scale));
        if (this.lightboxScale === 1) {
          this.lightboxX = 0;
          this.lightboxY = 0;
        }
        this.applyLightboxPinch();
      } else if (event.touches.length === 1 && this.lightboxPan && (this.lightboxScale || 1) > 1) {
        event.preventDefault();
        this.lightboxX = this.lightboxPan.originX + (event.touches[0].clientX - this.lightboxPan.x);
        this.lightboxY = this.lightboxPan.originY + (event.touches[0].clientY - this.lightboxPan.y);
        this.applyLightboxPinch();
      }
    }
    onLightboxTouchEnd(event) {
      if (event.touches.length < 2) this.lightboxPinching = false;
      if (event.touches.length === 0) this.lightboxPan = null;
      if ((this.lightboxScale || 1) <= 1.02) this.resetLightboxPinch();
    }
    applyLightboxPinch() {
      const image = this.querySelector('[data-zoom-image]');
      if (!image) return;
      const scale = this.lightboxScale || 1;
      const x = this.lightboxX || 0;
      const y = this.lightboxY || 0;
      image.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }
    resetLightboxPinch() {
      this.lightboxScale = 1;
      this.lightboxX = 0;
      this.lightboxY = 0;
      this.lightboxPinching = false;
      this.lightboxPan = null;
      const image = this.querySelector('[data-zoom-image]');
      if (image) image.style.transform = '';
    }
    openHelp() {
      const dialog = this.querySelector('[data-help-dialog]');
      if (!dialog?.showModal || dialog.open) return;
      this.restoreHelpProduct();
      dialog.showModal();
      dialog.querySelector('[data-help-close]')?.focus({ preventScroll: true });
    }
    closeHelp() {
      const dialog = this.querySelector('[data-help-dialog]');
      if (dialog?.open) dialog.close();
    }
    finishHelpResult() {
      const dialog = this.querySelector('[data-help-dialog]');
      const success = dialog?.querySelector('[data-help-success]');
      if (!success || success.hidden) return;
      success.hidden = true;
      success.inert = true;
      const fields = dialog.querySelector('[data-help-fields]');
      if (fields) {
        fields.hidden = false;
        fields.inert = false;
      }
      const message = dialog.querySelector('.help-dialog__message');
      const confirm = dialog.querySelector('[data-help-confirm]');
      if (message) message.value = '';
      if (confirm) confirm.value = '';
      this.clearContactPostedUrl();
    }
    clearContactPostedUrl() {
      const url = new URL(location.href);
      const postedKeys = [...url.searchParams.keys()].filter((key) => {
        if (key === 'contact_posted' || key.startsWith('contact[') || key.startsWith('contact.')) return true;
        return key === 'form_type' && url.searchParams.get(key) === 'contact';
      });
      const form = this.querySelector('.help-dialog__form');
      const postedHash = url.hash === '#contact_form' || (form?.id && url.hash === `#${form.id}`);
      if (!postedKeys.length && !postedHash) return;
      postedKeys.forEach((key) => url.searchParams.delete(key));
      if (postedHash) url.hash = '';
      history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
    restoreHelpProduct() {
      const card = this.querySelector('[data-help-product]');
      if (!card) return;
      card.hidden = false;
      this.writeHelpProduct(this.querySelector('[data-option-state]'));
    }
    syncHelpProduct(state) {
      const card = this.querySelector('[data-help-product]');
      if (!card || card.hidden) return;
      this.writeHelpProduct(state);
    }
    writeHelpProduct(state) {
      const dialog = this.querySelector('[data-help-dialog]');
      if (!dialog || !state) return;
      const detail = state.dataset.helpDetail || '';
      const title = dialog.dataset.productTitle || '';
      const color = dialog.querySelector('[data-help-color]');
      const image = dialog.querySelector('[data-help-image]');
      const field = dialog.querySelector('[data-help-product-field]');
      const variantField = dialog.querySelector('[data-help-variant-field]');
      if (color) {
        color.textContent = detail;
        color.hidden = detail === '';
      }
      if (image && state.dataset.helpImage) image.src = state.dataset.helpImage;
      if (field) {
        field.disabled = false;
        field.value = detail ? `${title} — ${detail}` : title;
      }
      if (variantField) {
        variantField.disabled = false;
        variantField.value = state.dataset.variantId || '';
      }
    }
    removeHelpProduct() {
      const dialog = this.querySelector('[data-help-dialog]');
      const card = dialog?.querySelector('[data-help-product]');
      if (card) card.hidden = true;
      const field = dialog?.querySelector('[data-help-product-field]');
      const variantField = dialog?.querySelector('[data-help-variant-field]');
      if (field) field.disabled = true;
      if (variantField) variantField.disabled = true;
    }
    submitHelp(event) {
      const form = event.currentTarget;
      const email = form.querySelector('[data-help-email]');
      const confirm = form.querySelector('[data-help-confirm]');
      if (confirm && email) {
        const same = confirm.value.trim().toLowerCase() === email.value.trim().toLowerCase();
        confirm.setCustomValidity(same ? '' : (confirm.dataset.mismatch || ''));
        if (!same) {
          event.preventDefault();
          confirm.reportValidity();
          return;
        }
      }
      const name = form.querySelector('[data-help-name]');
      if (name) {
        const first = form.querySelector('[data-help-first]')?.value.trim() || '';
        const last = form.querySelector('[data-help-last]')?.value.trim() || '';
        name.value = [first, last].filter(Boolean).join(' ');
      }
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.optionRequest?.abort(); this.galleryObserver?.disconnect();
      this.pinObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      const helpDialog = this.querySelector('[data-help-dialog]');
      if (helpDialog?.open) helpDialog.close();
    }
  }
  customElements.define('catalog-product', CatalogProduct);
})();
